/**
 * Reverse lookup from a streamed transfer `portId` to the station the scene
 * actually renders, so the OHT hoist can be lowered onto that station's deck.
 *
 * `useEquipmentFoupTransfer` answers the same question from the equipment's own
 * side, where the component already knows its element. The OHT overlay has only
 * the port id, and a lookup restricted to `state.layout.elements` misses overlay
 * equipment (whose ports live on an `EquipmentInstance`) and any backend port id
 * that does not match a stored layout port id verbatim.
 */
import type { LayoutElement } from '../../../../types';
import type {
  EquipmentInstance,
  EquipmentTemplate,
} from '../../../../features/equipment/equipmentTypes';
import { equipmentInstanceToLayoutElement } from '../../../../utils/equipmentGeometry';
import {
  canonicalEquipmentId,
  equipmentIdFromPortId,
  portsOf,
} from './equipmentPortOwnership';

export type StationPortIndex = {
  /** Exact port id → owning station. */
  byPortId: Map<string, LayoutElement>;
  /** Every alias of a station id (raw and canonical) → station. */
  byOwnerId: Map<string, LayoutElement>;
};

function ownerAliases(element: LayoutElement): string[] {
  return [
    element.id,
    element.metadata?.name ?? '',
    String(element.customProperties?.toolId ?? ''),
    String(element.customProperties?.amhsId ?? ''),
    String(element.customProperties?.loadPortId ?? ''),
    String(element.customProperties?.dxfEquipmentId ?? ''),
  ].filter((alias) => alias.length > 0);
}

export function buildStationPortIndex(
  elements: Record<string, LayoutElement> | undefined,
  instances: Record<string, EquipmentInstance> | undefined,
  templates: Record<string, EquipmentTemplate> | undefined,
  portOwners: Array<Record<string, string> | undefined> = []
): StationPortIndex {
  const byPortId = new Map<string, LayoutElement>();
  const byOwnerId = new Map<string, LayoutElement>();

  const register = (element: LayoutElement) => {
    for (const alias of ownerAliases(element)) {
      if (!byOwnerId.has(alias)) byOwnerId.set(alias, element);
      const canonical = canonicalEquipmentId(alias);
      if (canonical && !byOwnerId.has(canonical)) byOwnerId.set(canonical, element);
    }
    for (const port of portsOf(element, instances)) {
      if (typeof port?.id === 'string' && port.id) byPortId.set(port.id, element);
    }
  };

  for (const element of Object.values(elements ?? {})) register(element);

  // A layout element and an overlay instance can share an id; the layout element
  // is what `FactoryElement3D` draws, so it must win.
  for (const [id, instance] of Object.entries(instances ?? {})) {
    if (elements?.[id]) continue;
    const template = instance.templateId
      ? (templates?.[instance.templateId] ?? null)
      : null;
    register(equipmentInstanceToLayoutElement(instance, template));
  }

  for (const map of portOwners) {
    for (const [portId, ownerId] of Object.entries(map ?? {})) {
      if (!portId || !ownerId || byPortId.has(portId)) continue;
      const owner =
        byOwnerId.get(ownerId) ?? byOwnerId.get(canonicalEquipmentId(ownerId));
      if (owner) byPortId.set(portId, owner);
    }
  }

  return { byPortId, byOwnerId };
}

/**
 * Station a transfer is working, widening from the exact port id to the parent
 * encoded in `EQ-2-LP-1`, then to the event's `stationId`. Both fall back
 * through canonical ids so backend `EQ-2` matches a rendered `EQ2`.
 */
export function resolveTransferStation(
  index: StationPortIndex,
  portId: string | undefined | null,
  stationId?: string | null
): LayoutElement | undefined {
  if (portId) {
    const exact = index.byPortId.get(portId);
    if (exact) return exact;
  }

  const candidates = [equipmentIdFromPortId(portId), portId, stationId];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const owner =
      index.byOwnerId.get(candidate) ??
      index.byOwnerId.get(canonicalEquipmentId(candidate));
    if (owner) return owner;
  }
  return undefined;
}
